import { useState, useEffect } from 'react';
import { apiClient } from '../../utils/apiClient';
import { useNavigate } from 'react-router-dom';
import Toast from '../../components/shared/Toast';

const SOCIAL_PROVIDERS = [
  {
    id: 'google',
    name: 'Google',
    icon: '/icons/google-icon.svg',
    className: 'bg-white hover:bg-gray-50 text-gray-700',
  },
  {
    id: 'microsoft',
    name: 'Microsoft',
    icon: '/icons/microsoft-icon.svg',
    className: 'bg-[#2F2F2F] hover:bg-black text-white',
  },
  {
    id: 'apple',
    name: 'Apple',
    icon: '/icons/apple-icon.svg',
    className: 'bg-white hover:bg-gray-50 text-gray-700',
  },
];

const SignupPage = ({ onClose }) => {
  const navigate = useNavigate();
  const [formState, setFormState] = useState({
    email: '',
    isValidEmail: true,
    isLoading: false,
  });

  const validateEmail = (email) => {
    const regex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return regex.test(email);
  };

  const handleEmailChange = (e) => {
    const { value } = e.target;
    setFormState((prev) => ({
      ...prev,
      email: value,
      isValidEmail: true,
    }));
  };

  const handleEmailSubmit = async () => {
    const { email } = formState;

    if (!validateEmail(email)) {
      setFormState((prev) => ({ ...prev, isValidEmail: false }));
      return;
    }

    setFormState((prev) => ({ ...prev, isLoading: true }));
    sessionStorage.setItem('userEmail', email);

    try {
       // Handle affiliate referral if present (don't let this affect registration)
      const affiliate_code = localStorage.getItem('affiliate_ref');
      await apiClient.auth.register({
        email,
        ...(affiliate_code && { affiliate_code }),
      });
      
     
      if (affiliate_code) {
        setTimeout(async () => {
          try {
            const baseUrl = process.env.REACT_APP_API_URL;
            await fetch(`${baseUrl}/api/ai/affiliate/update-affiliate-registration`, {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json'
              },
              body: JSON.stringify({ affiliate_code, email })
            });
          } catch (affiliateError) {
            console.error('Affiliate update failed:', affiliateError);
          }
        }, 100);
      }
      
      Toast.success('Verification email sent successfully!');
      onClose();
      navigate('/email-confirmation', { state: { email } });
    } catch (error) {
      let errorMessage = 'Email verification failed';
      try {
        const parsedError = JSON.parse(error.message);
        errorMessage = parsedError.data?.error || errorMessage;
      } catch (parseError) {
        console.error(
          'Failed to parse error message:',
          error.message,
          parseError
        );
        errorMessage = error.message || errorMessage; // Fallback to raw message
      }
      Toast.error(errorMessage);
      console.error('Registration failed:', error);
    } finally {
      setFormState((prev) => ({ ...prev, isLoading: false }));
    }
  };

  const handleSocialLogin = (provider) => {
    try {
      onClose();
      const baseUrl = process.env.REACT_APP_API_URL;
      const affiliate_code = localStorage.getItem('affiliate_ref');

      if (affiliate_code) {
        document.cookie = `affiliate_ref=${affiliate_code}; path=/; max-age=86400; SameSite=Lax`;
        window.location.href = `${baseUrl}/auth/${provider}?affiliate_code=${encodeURIComponent(affiliate_code)}`;
      } else {
        window.location.href = `${baseUrl}/auth/${provider}`;
      }
    } catch (error) {
      Toast.error(`Failed to connect with ${provider}`);
    }
  };

  const { email, isValidEmail, isLoading } = formState;

  useEffect(() => {
    const affiliate_code = localStorage.getItem('affiliate_ref'); // Updated variable name
    if (affiliate_code) {
      console.log('Affiliate Code from localStorage:', affiliate_code); // Updated log message
    }
  }, []);

  return (
    <div className="w-full">
      <div className="space-y-6">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-gray-900">
            Create your free account
          </h2>
          <p className="text-gray-600 mt-2">
            100% free. No credit card needed.
          </p>
        </div>
        <div className="space-y-4">
          {SOCIAL_PROVIDERS.map((provider) => (
            <button
              key={provider.id}
              onClick={() => handleSocialLogin(provider.id)}
              disabled={isLoading}
              className={`w-full flex items-center justify-center space-x-3 py-3 px-4 border border-gray-300 rounded-md ${provider.className} transition-all duration-200 transform hover:scale-[1.02] hover:shadow-md disabled:opacity-50 disabled:cursor-not-allowed`}
            >
              <img
                src={provider.icon}
                alt={provider.name}
                className="w-5 h-5"
              />
              <span>Continue with {provider.name}</span>
            </button>
          ))}
        </div>
        <div className="relative">
          <div className="absolute inset-0 flex items-center">
            <div className="w-full border-t border-gray-200"></div>
          </div>
          <div className="relative flex justify-center text-sm">
            <span className="px-2 bg-white text-gray-500">Or</span>
          </div>
        </div>
        <div className="space-y-4">
          <div>
            <input
              type="email"
              placeholder="Email address"
              value={email}
              onChange={handleEmailChange}
              className={`w-full px-4 py-3 border ${
                isValidEmail
                  ? 'border-gray-300 focus:border-purple-500'
                  : 'border-red-500'
              } rounded-md focus:ring-2 focus:ring-purple-500 outline-none transition-all duration-200`}
            />
            {!isValidEmail && (
              <p className="mt-2 text-sm text-red-600">
                Please enter a valid email address
              </p>
            )}
          </div>
          <button
            onClick={handleEmailSubmit}
            disabled={isLoading}
            className="w-full py-3 px-4 bg-purple-600 hover:bg-purple-700 text-white rounded-md transition-all duration-200 transform hover:scale-[1.02] focus:ring-2 focus:ring-purple-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
          >
            {isLoading ? (
              <svg
                className="animate-spin h-5 w-5 text-white"
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
              >
                <circle
                  className="opacity-25"
                  cx="12"
                  cy="12"
                  r="10"
                  stroke="currentColor"
                  strokeWidth="4"
                ></circle>
                <path
                  className="opacity-75"
                  fill="currentColor"
                  d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                ></path>
              </svg>
            ) : (
              'Verify email'
            )}
          </button>
        </div>
        <p className="text-sm text-center text-gray-600">
          We're committed to your privacy. PathFinder uses the information you
          provide to contact you about our relevant content, products, and
          services. You may unsubscribe from these communications at any time.
          For more information, check out our{' '}
          <a href="/privacy" className="text-purple-600 hover:underline">
            Privacy Policy
          </a>
        </p>
      </div>
    </div>
  );
};

export default SignupPage;